import { checkLicense } from '@jira/spa-apps__interceptors/src/checkLicense.tsx';
// eslint-disable-next-line jira/import-whitelist
import { JIRA_PROJECT_TYPE_SERVICE_DESK_PROJECT } from '@jira/platform__project-types/src/project-types.tsx';
import type { FeaturesRestData } from '@jira/router-resources__service-project-features/src/FeaturesData.types.tsx';
import { RESOLVE_WITH_ROVO_FEATURE } from '@jira/router-resources__service-project-features/src/resolveWithRovoFeature.tsx';
// eslint-disable-next-line jira/import-whitelist
import { ENABLED, JSM_AI_CONTEXT } from '@jira/servicedesk__common/src/Enabled.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { performGetRequest } from '@atlassian/jira-fetch';
import {
	createResource,
	type RouterDataContext,
	type ResourceStoreContext,
	type RouteResource,
} from '@atlassian/react-resource-router';
import { RESOURCE_TYPE_RESOLVE_WITH_ROVO } from './resourceTypeContextPanelIssueDetails.constants.tsx';
import { getIssueDetails } from './getIssueDetails.tsx';

export type ResolveWithRovoResult = {
	enabled: boolean;
	isAiContextFeatureEnabled?: boolean;
} | null;

// SSR-friendly source of truth for the `resolve-with-rovo` per-project setting on
// the issue view. `useServiceProjectFeatures()` (the settings-page source) is NOT a
// route resource on the issue view, so it only resolves after a client fetch — which
// makes the AI summary section / legacy panels flip visibility on first paint. This
// resource is loaded as part of the issue-view route so both `AISummarySection` and
// `AIContextPanel` can read the value synchronously at hydration (no flicker).
//
// It self-guards: it returns `null` (which reads as "not enabled") unless the
// `ai_summary_section` gate is on, the site has a service desk license, and the
// issue belongs to a service desk project. Project type is read via `getIssueDetails`
// so the service-desk-only feature-state lookup is never called for non-JSM issues
// opened via `/browse`.
export const resolveWithRovoResource: RouteResource<ResolveWithRovoResult> =
	createResource<ResolveWithRovoResult>({
		type: RESOURCE_TYPE_RESOLVE_WITH_ROVO,
		getKey: ({ match, query }) => {
			const issueKey = match.params.issueKey || query.selectedIssue || '';
			const projectKey = issueKey.split('-')[0] || issueKey;
			return `resolveWithRovoResource-${projectKey}`;
		},
		getData: async (
			routerDataContext: RouterDataContext,
			resourceStoreContext: ResourceStoreContext,
		) => {
			const { match, query } = routerDataContext;
			const { tenantContext } = resourceStoreContext;
			if (!fg('ai_summary_section')) {
				return null;
			}
			if (!checkLicense(tenantContext, 'servicedesk')) {
				return null;
			}

			const issueKey = match.params.issueKey || query.selectedIssue || '';
			const { cloudId } = tenantContext;
			if (!issueKey || !cloudId) {
				return null;
			}

			const issueDetails = await getIssueDetails(issueKey, cloudId, true);
			const projectKey = issueDetails.projectKey;
			if (issueDetails.projectType !== JIRA_PROJECT_TYPE_SERVICE_DESK_PROJECT || !projectKey) {
				return null;
			}

			try {
				const features = await performGetRequest<FeaturesRestData>(
					`/rest/servicedesk/1/servicedesk/${projectKey}/features`,
				);
				const resolveWithRovo = features.find(
					({ feature }) => feature === RESOLVE_WITH_ROVO_FEATURE,
				);
				const aiContextFeature = features.find(({ feature }) => feature === JSM_AI_CONTEXT);
				const isAiContextFeatureEnabled = aiContextFeature?.status === ENABLED;
				return {
					enabled: resolveWithRovo?.status === ENABLED,
					isAiContextFeatureEnabled,
				};
			} catch {
				// Fail safe to "not enabled" so we fall back to the legacy panels rather than
				// hiding everything if the features endpoint is unavailable.
				return { enabled: false };
			}
		},
		// Align with `contextPanelIssueDetailsResource` (60m). `resolve-with-rovo` is a
		// rarely-changed per-project admin setting, so a short TTL would re-fire the
		// uncached `/features` REST call far more often than the value actually changes.
		maxAge: 60 * 60 * 1000,
	});
